import type { CrmVersionenListe } from '@portal/contracts';
import { VERSION_TABELLEN } from '@portal/contracts';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { crmApi, HttpError } from '../../api.js';
import { onLinkClick } from '../../router.js';
import { Problem } from '../ModulePage.js';
import { CRM_PFAD, TABELLE_LABEL } from './crm-felder.js';
import { Filter, Seiten, VersionenTabelle } from './Versionen.js';

/**
 * `/crm/protokoll` — Änderungsprotokoll über alle Datensätze: Filter Zeitraum von/bis, Akteur, Tabelle; Seiten.
 * Personen werden nachgeladen, um den Namen zu verlinken; gelöschte Personen stehen als „gelöscht“.
 */
export function Protokoll() {
  const [filter, setFilter] = useState({ von: '', bis: '', akteur: '', tabelle: '', seite: 1 });
  const [daten, setDaten] = useState<CrmVersionenListe | null>(null);
  const [personen, setPersonen] = useState<Map<string, string | null>>(new Map());
  const [fehler, setFehler] = useState<number | null>(null);
  /** Personen, deren Name wegen eines temporären Fehlers (nicht 404) fehlt — kein Cache, erneut ladbar */
  const [namenFehler, setNamenFehler] = useState(0);
  const [versuch, setVersuch] = useState(0);
  const tabelleId = useId();

  useEffect(() => {
    let alive = true;
    setFehler(null);
    const q = { ...filter, von: filter.von ? new Date(filter.von).toISOString() : '', bis: filter.bis ? new Date(filter.bis).toISOString() : '' };
    crmApi
      .versionen(q)
      .then(async (d) => {
        if (!alive) return;
        setDaten(d);
        // Namen der betroffenen Personen (nur die noch unbekannten)
        const ids = [...new Set(d.eintraege.map((v) => v.personId).filter((x): x is string => Boolean(x)))].filter((x) => !personen.has(x));
        setNamenFehler(0);
        if (!ids.length) return;
        const neu = new Map(personen);
        let offen = 0;
        await Promise.all(
          ids.map(async (id) => {
            try {
              const p = await crmApi.person(id);
              neu.set(id, [p.vorname, p.nachname].filter(Boolean).join(' ') || p.name || p.firma || '');
            } catch (err) {
              // Nur 404 heißt „gelöscht“; Netz-/Rechte-/Serverfehler werden nicht gemerkt (Ladezustand bleibt, erneut versuchen)
              if (err instanceof HttpError && err.status === 404) neu.set(id, null);
              else offen++;
            }
          }),
        );
        if (!alive) return;
        setPersonen(neu);
        setNamenFehler(offen);
      })
      .catch((err: unknown) => alive && setFehler(err instanceof HttpError ? err.status : 0));
    return () => {
      alive = false;
    };
    // personen bewusst nicht als Abhängigkeit (Cache)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, versuch]);

  if (fehler === 403 || fehler === 401 || fehler === 503) return <Problem status={fehler} />;

  return (
    <section className="crm" aria-labelledby="crm-protokoll-titel">
      <a className="crm-back" href={CRM_PFAD} onClick={onLinkClick(CRM_PFAD)}>
        <ArrowLeft size={15} aria-hidden /> Zur Kundenliste
      </a>
      <div className="ap-head">
        <div>
          <p className="crm-crumb">CRM</p>
          <h1 className="ap-title" id="crm-protokoll-titel">
            Änderungsprotokoll
          </h1>
          <p className="ap-sub">Jede Änderung an Kundendaten — lückenlos, unveränderlich, egal wer sie vorgenommen hat.</p>
        </div>
      </div>
      <div className="crm-filterleiste">
        <Filter label="Von" typ="datetime-local" wert={filter.von} onChange={(von) => setFilter({ ...filter, von, seite: 1 })} />
        <Filter label="Bis" typ="datetime-local" wert={filter.bis} onChange={(bis) => setFilter({ ...filter, bis, seite: 1 })} />
        <Filter label="Akteur" wert={filter.akteur} onChange={(akteur) => setFilter({ ...filter, akteur, seite: 1 })} />
        <div className="crm-feld crm-filter">
          <label htmlFor={tabelleId}>Tabelle</label>
          <select id={tabelleId} value={filter.tabelle} onChange={(e) => setFilter({ ...filter, tabelle: e.target.value, seite: 1 })}>
            <option value="">Alle</option>
            {VERSION_TABELLEN.map((t) => (
              <option key={t} value={t}>
                {TABELLE_LABEL[t] ?? t}
              </option>
            ))}
          </select>
        </div>
      </div>
      {fehler !== null ? (
        <p className="crm-hinweis crm-hinweis--fehler" role="alert">
          Das Änderungsprotokoll konnte nicht geladen werden. Bitte prüfen Sie die Filter oder versuchen Sie es erneut.
        </p>
      ) : !daten ? (
        <p className="sh-status" aria-busy="true">
          Änderungsprotokoll wird geladen …
        </p>
      ) : (
        <>
          {namenFehler > 0 && (
            <p className="crm-hinweis crm-hinweis--fehler" role="alert">
              {namenFehler === 1 ? 'Ein Personenname' : `${namenFehler} Personennamen`} konnte{namenFehler === 1 ? '' : 'n'} gerade nicht geladen werden — die Einträge zeigen solange die Kennung.{' '}
              <button type="button" className="pb-button pb-button--quiet" onClick={() => setVersuch((n) => n + 1)}>
                Erneut laden
              </button>
            </p>
          )}
          <VersionenTabelle daten={daten} mitPerson personen={personen} />
          <Seiten seite={daten.seite} groesse={daten.groesse} gesamt={daten.gesamt} onSeite={(seite) => setFilter({ ...filter, seite })} />
        </>
      )}
    </section>
  );
}
